import {
  DatePickerField,
  DateRangePickerField,
  Form,
  SubmitButton,
  TextInputField,
} from '@/components';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  type SnapshotRequestPayloadType,
  useCreateSnapshot,
  useUpdateSnapshot,
} from '../../_hooks/useSnapshot';
import { SnapshotFormSchema } from '../../_model/snapshot';
import { useSnapshotDrawerState } from './SnapshotDrawerStateProvider';

type FormValues = z.infer<typeof SnapshotFormSchema>;

type Props = {
  initialValues?: Partial<FormValues>;
  editTargetId: string | undefined;
};

const MIN_DATE = dayjs('2024-04-01');

export const SnapshotForm: FC<Props> = ({ editTargetId, initialValues }) => {
  const { t } = useTranslation();
  const { setDrawerState } = useSnapshotDrawerState();
  const { mutateAsync: createSnapshot } = useCreateSnapshot();
  const { mutateAsync: updateSnapshot } = useUpdateSnapshot();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(SnapshotFormSchema),
  });

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const payload: SnapshotRequestPayloadType = {
        name: values.name,
        referenceDate: values.referenceDate?.format('YYYY-MM-DD'),
        startYear: values.period?.[0]?.year() ?? null,
        endYear: values.period?.[1]?.year() ?? null,
      };
      try {
        if (editTargetId) {
          await updateSnapshot({ id: editTargetId, ...payload });
        } else {
          await createSnapshot(payload);
        }
        setDrawerState({ open: false });
      } catch {
        //
      }
    },
    [editTargetId, createSnapshot, setDrawerState, updateSnapshot],
  );

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <DatePickerField
        name="referenceDate"
        label={t('snapshot.date')}
        control={control}
        errors={errors}
        required
        disabled={!!editTargetId}
        disabledDate={(current) => {
          return current?.isBefore(MIN_DATE) || current?.isAfter(dayjs());
        }}
      />
      <TextInputField
        name="name"
        label={t('name')}
        control={control}
        errors={errors}
        autocompleteKey="snapshot-name"
        required
      />
      <DateRangePickerField
        name="period"
        label={t('start-and-end-year')}
        control={control}
        errors={errors}
        picker="year"
        allowEmpty={[true, true]}
      />
      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
